import { BufferGeometry, Group, Mesh, Object3D, Quaternion, Vector3 } from 'three'
import type { Level } from '../core/levelData'
import type { GameSession } from '../game/session'
import type { RoadSpline } from '../core/spline/splineGenerator'
import { convertPosition, convertQuaternion, unityLookRotation } from '../core/unity'
import type { AssetLibrary, ManifestNode } from './assetLibrary'
import { buildPrefab } from './prefabBuilder'
import { buildSplineRoad, type SplineRoad } from './splineRoad'
import { ConveyorIndicatorView } from './conveyorIndicatorView'

/**
 * Everything on screen that is not a bus or a person.
 *
 * Three sources, all authored, none of them invented here:
 *
 *  - **`Gameplay.unity`.** The ground plate, the raised platform the stops sit
 *    on, the four road stubs that run off the top of the level and the kerbside
 *    prop are scene objects at fixed world transforms. They are identical in
 *    every level, so they are built once and kept.
 *  - **`WaitingArea.prefab`.** The shelter, its ground plate and the two sliding
 *    doors, instantiated once per `WaitingAreaData` in the level.
 *  - **The spline.** The loop road itself is a Dreamteck `SplineMesh`, which
 *    the shipped scene carries only as a baked `Custom Mesh` — so it is swept
 *    again here from `Road.obj`, the tile it was baked from.
 *
 * Colour comes from the vertex-colour palette in every case (`prefabBuilder`
 * resolves it), which is why every one of these materials is white in the
 * manifest.
 */

/**
 * Scene roots that are not dressing.
 *
 * The camera and light are read separately, the controllers are behaviour, the
 * tutorial is switched off, and the two `SplineMesh` nodes hold baked meshes
 * (`Custom Mesh`, `Custom Mesh_1`) that AssetRipper could not export — the road
 * is regenerated from its tile instead. `ConveyorIndicator` is dressing but
 * moves per level, so it is placed by `build`, not carried in the static tree.
 */
const _coverPos = new Vector3()
const _coverDir = new Vector3()
const _coverRot = new Quaternion()

/** One of the two `Lift_Lid_*` covers of a layer, with the scale it rests at. */
interface ElevatorLidView {
  node: Object3D
  /**
   * `localScale.z` as `ElevatorLayer.prefab` ships it — 🔍 0.13865145 for both
   * lids (prefab `:223`, `:291`), under `Ground` at scale 10. `DOScaleZ(0f, …)`
   * tweens from *whatever the transform holds* down to 0, and nothing else
   * writes it, so this is where the retraction starts.
   */
  restScaleZ: number
  /**
   * Whether the lid is shown at all while its layer is not yet done — true
   * except on the bottom layer, whose lids `ToggleLastLayerVisual` switches
   * off in favour of the mezzanine (ELV-5). Once the layer is done the lid is
   * switched off regardless (`<CompleteLayer>b__22_0`), see `syncElevators`.
   */
  restVisible: boolean
}

/** One drawn `ElevatorLayer`, with the two lids that retract when it is done. */
interface ElevatorLayerView {
  node: Object3D
  lids: ElevatorLidView[]
}

interface ElevatorView {
  pivot: Object3D
  layers: ElevatorLayerView[]
}

/** 🔍 `GenerateLayers`: `localPosition = (0, i * -8f, 0)`. */
const ELEVATOR_LAYER_DROP = 8

/**
 * 🔍 `ElevatorLayer.CompleteLayer` (`__0x211b480.c`), per cover:
 *
 *     _coverSequence.Insert(0.446f, cover.transform.DOScaleZ(0f, 0.446f));
 *
 * Both literals are the one constant 0x3EE45A1D = 0.446. `Sequence.Insert(this
 * Sequence, float atPosition, Tween)` has exactly one float, so the first is
 * `atPosition` and the second `DOScaleZ`'s duration — there is no reading of
 * the call in which the lids move before 0.446 s. The lids therefore sit at
 * rest for the first half of `ELEVATOR_COVER_SECONDS` (0.892 s, the rules'
 * clock in `GameSession.coverProgress`) and retract over the second half.
 */
const ELEVATOR_COVER_INSERT_AT = 0.446
const ELEVATOR_COVER_TWEEN_SECONDS = 0.446

const SKIP_SCENE_ROOTS: ReadonlySet<string> = new Set([
  'Main Camera',
  'Directional Light',
  'Controllers',
  'GameplayTutorial',
  'SplineMesh',
  'SplineMesh (1)',
  'ConveyorIndicator',
])

/** Prefabs and scene nodes whose meshes have to be resident before building. */
export function environmentMeshes(library: AssetLibrary): string[] {
  const names = new Set<string>(['Road', 'RoadLine'])
  const scene = library.scene
  if (scene) library.meshesUsedBy(scene, names)
  for (const key of ['WaitingArea', 'Elevator', 'ElevatorLayer', 'ConveyorCover'] as const) {
    const node = library.prefab(key)
    if (node) library.meshesUsedBy(node, names)
  }
  return [...names]
}

export interface EnvironmentTuning {
  /**
   * How far apart the centre-line dashes sit, measured between dash centres.
   *
   * `RoadLine.obj` is a 1.44-long dash and the shipped scene bakes it into the
   * road's `Custom Mesh` as a second Dreamteck channel, so the spacing the
   * author picked is not recoverable — the channel's settings went with the
   * stripped `SplineMesh` component. 2.88 makes dash and gap equal, which is
   * what the reference screenshot shows.
   */
  dashSpacing: number
  /**
   * Height the dashes are laid at.
   *
   * The road tile's driving surface is flat at y = 0.128 and `RoadLine.obj` is
   * flat at y = 0, so the dash needs lifting onto it. The extra hundredth keeps
   * it off the surface at grazing angles.
   */
  dashHeight: number
}

export const DEFAULT_ENVIRONMENT_TUNING: EnvironmentTuning = {
  dashSpacing: 2.88,
  dashHeight: 0.138,
}

export class EnvironmentView {
  readonly object = new Group()

  /** Rebuilt per level; the scene dressing under `static` is not. */
  private readonly perLevel = new Group()

  /** One per elevator, in level-data order. Rebuilt with the level. */
  private readonly elevatorViews: ElevatorView[] = []
  private road: SplineRoad | null = null
  /** The capacity sign, when the level places one. Rebuilt with the level. */
  private indicator: ConveyorIndicatorView | null = null
  private readonly doors: {
    left: Object3D
    right: Object3D
    /** The authored closed pose, which `OpenExitDoors` measures its offsets from. */
    closed: { x: number; z: number }
  }[] = []

  constructor(
    private readonly library: AssetLibrary,
    private readonly tuning: EnvironmentTuning = DEFAULT_ENVIRONMENT_TUNING,
  ) {
    this.object.name = 'environment'

    const dressing = new Group()
    dressing.name = 'sceneDressing'
    const scene = library.scene
    if (scene) {
      for (const child of scene.children) {
        if (!child.active || SKIP_SCENE_ROOTS.has(child.name)) continue
        dressing.add(buildPrefab(library, child))
      }
    }
    this.object.add(dressing)
    this.object.add(this.perLevel)
  }

  /** Swap in the dressing that depends on the level. */
  build(level: Level, road: RoadSpline): void {
    this.clearPerLevel()

    const swept = buildSplineRoad(this.library, road, this.tuning)
    if (swept) {
      this.road = swept
      this.perLevel.add(swept.object)
    }

    for (const area of level.waitingAreas) {
      const node = this.library.prefab('WaitingArea')
      if (!node) break
      const station = buildPrefab(this.library, node)
      convertPosition(area.position, station.position)
      convertQuaternion(area.rotation, station.quaternion)
      this.perLevel.add(station)

      // `WaitingArea.OpenExitDoors` slides the two leaves apart. They are two
      // halves of one doorway — `Door1` spans x −3.51…0 and `Door2` mirrors it —
      // so each retreats along its own side. `Door1` is therefore `doorLeft`,
      // the one the decomp moves to `closed.x - doorOpenSlideDistance`; the
      // other assignment would slide the leaves *through* each other.
      //
      // Both are authored at local (0, 0, -7.2755); read it rather than assume.
      const left = station.getObjectByName('Door1')
      const right = station.getObjectByName('Door2')
      if (left && right) {
        this.doors.push({
          left,
          right,
          closed: { x: right.position.x, z: right.position.z },
        })
      }
    }

    // The bus-stop sign, where the level puts it rather than where the scene
    // parks it. All 40 shipped levels carry a `ConveyorIndicatorPosition` — the
    // old note here said nine of them did not, which was measured against the
    // blended `bustraffic_kit/data/levels/` that `CLAUDE.md` forbids.
    const indicator = this.sceneChild('ConveyorIndicator')
    if (indicator && level.conveyorIndicatorPosition) {
      // `includeInactive`, unlike everything else built from the scene: the sign
      // needs `GlowParent`, and `GlowParent` is `m_IsActive: 0` there precisely
      // because `ConveyorIndicator.StartAlarm` is what switches it on.
      const sign = buildPrefab(this.library, indicator, { includeInactive: true })
      convertPosition(level.conveyorIndicatorPosition, sign.position)
      this.perLevel.add(sign)
      this.indicator = new ConveyorIndicatorView(sign, this.library.material('RoadMain'))
    }

    this.buildCovers(level, road)

    this.buildElevators(level)
  }

  /**
   * The elevator, with its stack of layers.
   *
   * 🔍 `Elevator.prefab` has two children, `LayerPivot` and `Lift`, and
   * `ElevatorController.GenerateLayers` instantiates one `ElevatorLayer` under
   * the pivot per layer at `localPosition = (0, i * -8f, 0)` — the stack hangs
   * **below** the lot and the pivot carries it up. `ElevatorLayer.prefab` in
   * turn is `Ground` + `Lift_Lid_1` + `Lift_Lid_2` + `Mezzanine` (which ships
   * inactive: it is `lastLayerVisual`, and `ToggleLastLayerVisual` switches it
   * on for the bottom-most layer only).
   *
   * Only the pivot and the layers were missing before T14c — the shaft has been
   * drawn all along, but bolted to the ground, so nothing about it moved when
   * the platform did.
   */
  private buildElevators(level: Level): void {
    const shaftNode = this.library.prefab('Elevator')
    const layerNode = this.library.prefab('ElevatorLayer')
    if (!shaftNode) return

    for (const elevator of level.elevators) {
      const shaft = buildPrefab(this.library, shaftNode)
      convertPosition(elevator.position, shaft.position)
      convertQuaternion(elevator.rotation, shaft.quaternion)
      this.perLevel.add(shaft)

      const pivot = shaft.getObjectByName('LayerPivot')
      if (!pivot || !layerNode) continue

      const layers: ElevatorLayerView[] = []
      elevator.layers.forEach((_, index) => {
        // 🔍 `includeInactive`: `Mezzanine` ships `m_IsActive: 0`
        // (`ElevatorLayer.prefab`:92) precisely because
        // `ToggleLastLayerVisual` is what switches it on — `SetActive(*, 1, 0)`
        // is the one argument Ghidra kept in that function. Built the default
        // way the node is skipped, `getObjectByName('Mezzanine')` is undefined,
        // and the bottom layer hides its lids over nothing (ELV-5).
        const built = buildPrefab(this.library, layerNode, { includeInactive: true })
        built.name = `ElevatorLayer-${index}`
        built.position.y = -ELEVATOR_LAYER_DROP * index
        pivot.add(built)

        // `ToggleLastLayerVisual()`: `lastLayerVisual.SetActive(true)` and the
        // covers go the other way — the bottom layer has nothing under it to
        // cap, so it shows its mezzanine floor instead of a pair of lids.
        const isLast = index === elevator.layers.length - 1
        const mezzanine = built.getObjectByName('Mezzanine')
        if (mezzanine) mezzanine.visible = isLast
        const lids = ['Lift_Lid_1', 'Lift_Lid_2']
          .map((name) => built.getObjectByName(name))
          .filter((o): o is Object3D => o !== undefined)
        for (const lid of lids) lid.visible = !isLast

        layers.push({
          node: built,
          lids: lids.map((node) => ({ node, restScaleZ: node.scale.z, restVisible: node.visible })),
        })
      })
      this.elevatorViews.push({ pivot, layers })
    }
  }

  /**
   * Follow the platform. `layerPivot.DOLocalMoveY(_currentLayer * 8f, 0.446f)`
   * and, on the layer being retired, `cover.transform.DOScaleZ(0f, 0.446f)`.
   *
   * Both clocks live in the rules (`ElevatorRuntime.pivotY`,
   * `GameSession.coverProgress`) because the same clocks decide when a bus may
   * be clicked. All that is left here is to apply them.
   *
   * ❓ The `SetEase` on `DOLocalMoveY` is erased, so the slide is linear — see
   * checklist §14.
   */
  syncElevators(session: GameSession): void {
    session.elevators.forEach((elevator, index) => {
      const view = this.elevatorViews[index]
      if (!view) return
      view.pivot.position.y = elevator.pivotY

      // The lids of the layer that is finishing retract to nothing. Everything
      // else keeps whatever `buildElevators` gave it.
      //
      // 🔍 `ElevatorLayer.CompleteLayer` (`__0x211b480.c`):
      //     _coverSequence.Insert(0.446f, cover.transform.DOScaleZ(0f, 0.446f))
      // a tween from the transform's current `localScale.z` — the prefab's
      // 0.13865145, see `ElevatorLidView.restScaleZ` — to 0. It used to be
      // `1 - progress`, which is 7.21× the rest scale on the first frame: the
      // lid (3.395 u deep in `Lift_Lid_1.obj`, ×10 from `Ground`) snapped from
      // 4.7 u to 33.9 u before shrinking (ELV-3). And it used to run from
      // `rising = 0`, i.e. over the whole 0.892 s — but the tween is
      // *inserted* at 0.446, so the lid holds its rest scale until then and
      // only retracts during the last 0.446 s (ELV-4). `coverProgress` is
      // untouched: it is the rules' clock for the whole sequence, and the
      // platform still waits for all of it. The straight line between the two
      // endpoints shares §14's ❓ on the erased `SetEase`.
      //
      // 🔴 **Every layer is written, every call, from the rules' state alone
      // (ELV-10).** This used to `return` when `coverProgress < 0` and touch
      // `layers[currentLayer]` only, which made the lids a *sampled* clock:
      // they held whatever the last call left. Two things went wrong with
      // that. `App.seek` and `renderPreview` did not call this per frame, so
      // a layer that finished inside the seek window rode up at rest size.
      // And even stepped per frame, `updateElevators` bumps `_currentLayer`
      // in the same update that takes `rising` past 0.892, so the frame that
      // would have written 0 wrote it to the **next** layer's lids and left
      // the finished one at 0.0027 (the 0.883 s frame) — invisible only
      // because every elevator in this bundle has two layers and the last
      // layer draws its mezzanine instead of lids.
      //
      // What the original guarantees: `DOScaleZ(0f)` ends on exactly 0 and
      // nothing restores a lid, and `_currentLayer++` runs in the sequence's
      // `OnComplete`. So a layer below `currentLayer` is done (0), the layer
      // *at* `currentLayer` is mid-sequence only while `rising` is still
      // inside the cover window (it has not been incremented yet), and every
      // other layer rests. A single call after a seek and sixty calls a
      // second now give the same picture.
      const coverProgress = session.coverProgress(elevator)
      const tweening = coverProgress >= 0 && coverProgress < 1
      const retract = Math.min(
        1,
        Math.max(0, (elevator.rising - ELEVATOR_COVER_INSERT_AT) / ELEVATOR_COVER_TWEEN_SECONDS),
      )
      view.layers.forEach((layer, layerIndex) => {
        const done = layerIndex < elevator.currentLayer
        let scale = 1
        if (done) scale = 0
        else if (layerIndex === elevator.currentLayer && tweening) scale = 1 - retract
        for (const lid of layer.lids) {
          lid.node.scale.z = lid.restScaleZ * scale
          // A done layer's lids are switched **off**, not merely scaled to
          // nothing. 🔍 `<CompleteLayer>b__22_0` (`__0x211bba4.c`), the
          // sequence's `OnComplete`: `collider.enabled = false` — the one
          // literal Ghidra kept — then `ground.gameObject.SetActive(…)` and
          // `cover.gameObject.SetActive(…)` for every cover, the same shape as
          // `ToggleLayer(bool)`. 📐 The bool is dropped with the call, and
          // `false` is the only value that does anything on a lid already
          // active and just tweened to 0. It matters on screen: `Lift_Lid_*`
          // has a chamfer strip whose faces keep their x–y extent at
          // `scale.z = 0`, so a scaled-but-active lid still rasterised as a
          // hairline across the shaft mouth — the "two faint stripes" the
          // user saw on level 14 once the top layer had gone (ELV-11).
          lid.node.visible = done ? false : lid.restVisible
        }
      })
    })
  }

  /**
   * The two tunnel mouths that cap the road, per `SplineGenerator.GenereteCovers`.
   *
   * One `ConveyorCover` at each end of the *drawn* road — `OriginalStartPercent`
   * and `OriginalEndPercent` — pushed two units along the spline toward the
   * middle and one unit up, facing the way traffic runs through it:
   *
   *     position = spline.Evaluate(percent).position + direction * 2 + Vector3.up
   *     rotation = Quaternion.LookRotation(direction, Vector3.up)
   *     localScale = Vector3.one
   *
   * The end cover takes the negated tangent, so both mouths face inward along
   * the road. Its child is literally named `ConveyorClose`; the sibling `Cube`
   * carries `Invisible2` and no mesh, so it draws nothing — it is the blocker
   * that stops a bus driving out through the wall.
   *
   * Skipped when the loop is closed — `Initialize` passes `SplineIsClosed`
   * straight through as `GenereteCovers`' early-out, and a closed loop has no
   * ends to cap. No level in this bundle is closed, so in practice all forty
   * draw both; the branch is here because the shipped code has it.
   */
  private buildCovers(level: Level, road: RoadSpline): void {
    if (level.splineIsClosed) return
    const node = this.library.prefab('ConveyorCover')
    if (!node) return

    const ends: readonly (readonly [number, number])[] = [
      [road.originalStartPercent, 1],
      [road.originalEndPercent, -1],
    ]
    for (const [percent, sign] of ends) {
      road.spline.evaluatePosition(percent, _coverPos)
      road.spline.evaluateTangent(percent, _coverDir).multiplyScalar(sign)
      // Unity falls back to Vector3.forward / back when the tangent degenerates.
      if (_coverDir.lengthSq() < 1e-6) _coverDir.set(0, 0, sign)
      _coverDir.normalize()
      _coverPos.addScaledVector(_coverDir, 2)
      _coverPos.y += 1

      const cover = buildPrefab(this.library, node)
      // Instantiate(prefab, position, rotation) overrides whatever pose the
      // prefab root was authored at, which is out at (-16.4, -5.2, 15.5).
      convertPosition(_coverPos, cover.position)
      convertQuaternion(unityLookRotation(_coverDir, _coverRot), cover.quaternion)
      cover.scale.setScalar(1)
      this.perLevel.add(cover)
    }
  }

  /**
   * Apply the door `Sequence` the rules are running — see
   * `WaitingArea.updateDoors`. The rules own the clock because the tween has
   * an interrupt rule (`Kill` mid-flight, restart from where it stands) that
   * would be invented if the view re-derived it from a boolean.
   *
   * Mind the two coordinate spaces. `doorOffsetX` / `doorOffsetZ` are deltas in **Unity**
   * space — they come straight out of `doorOpenSlideDistance` (1.5) and
   * `doorOpenSlideZ` (-0.5), the two `.ctor` field initialisers at 0x30/0x34.
   * `door.closed` was read off a node `buildPrefab` had already put through
   * `unityPosition`, i.e. `core/unity.ts`'s mirror `(x, y, z) -> (x, y, -z)`.
   * So X carries over unchanged, but **Z must be negated** the same way the
   * mirror negates it — adding a Unity delta to a mirrored coordinate walks the
   * leaves the wrong way.
   *
   * That is what the port was doing, and it was visible: `Door1`/`Door2` are
   * authored at local z -7.275499 under a `BusStation` parent at z +2.6399994,
   * so root-local z -4.6355; the station mesh's own AABB (centre z -3.372407,
   * extent 4.4315553 in `Mesh/BusStation.asset`) puts the front wall at root
   * z -5.164, and `BoardingPoint` — the side facing the road — is at z -7.2.
   * The leaves therefore have to step to -5.1355 (out through the wall plane,
   * towards the road) before parting sideways. `closed.z + doorOffsetZ` sent
   * them to -4.1355 instead: backwards, into the room.
   */
  syncDoors(areas: readonly { doorOffsetX: number; doorOffsetZ: number }[]): void {
    this.doors.forEach((door, i) => {
      const area = areas[i]
      if (!area) return
      door.left.position.x = door.closed.x - area.doorOffsetX
      door.right.position.x = door.closed.x + area.doorOffsetX
      door.left.position.z = door.closed.z - area.doorOffsetZ
      door.right.position.z = door.closed.z - area.doorOffsetZ
    })
  }

  private sceneChild(name: string): ManifestNode | null {
    return this.library.scene?.children.find((c) => c.name === name) ?? null
  }

  /**
   * `SplineController.RefreshConveyorIndicator` → `ConveyorIndicator.UpdateIndicator`.
   *
   * The rules already compute the sign's state; this hands it to the view once a
   * frame so the alarm and the pulse can run.
   */
  syncIndicator(session: GameSession, dt: number): void {
    const sign = session.conveyorIndicator
    this.indicator?.update(sign.current, sign.max, sign.spaceMaxed, dt)
  }

  private clearPerLevel(): void {
    // The kerb colour lives on a shared material, so a level torn down mid-alarm
    // would leave the next one's road pink.
    this.indicator?.reset()
    this.indicator = null
    disposeCloned(this.perLevel, this.library)
    this.perLevel.clear()
    this.road?.dispose()
    this.road = null
    this.doors.length = 0
    // 🔍 `BusController.LevelLoaded` (`__0x2105a48.c`:109-163) walks
    // `_elevators` (0x70), `Destroy`s every controller, then assigns a fresh
    // list. This array is that list. It used to survive the level: the shafts
    // went out with `perLevel`, but `syncElevators` indexes by elevator number,
    // so from the second elevator level on it drove the orphaned pivots of the
    // level before and the new platform never moved (ELV-2).
    this.elevatorViews.length = 0
  }

  dispose(): void {
    this.clearPerLevel()
    disposeCloned(this.object, this.library)
    this.object.removeFromParent()
  }
}

/**
 * Free the geometry this subtree owns, and only that.
 *
 * `buildPrefab` hands back the library's cached geometry untouched unless the
 * material carries a vertex-colour palette, in which case it clones first so
 * two instances cannot fight over one mask. The clone belongs to the subtree;
 * the cached original belongs to the library and outlives every level. The mesh
 * name it came from is on `userData`, which is what tells the two apart.
 */
function disposeCloned(root: Object3D, library: AssetLibrary): void {
  root.traverse((child) => {
    const mesh = child as Mesh
    const geometry = mesh.geometry as BufferGeometry | undefined
    if (!geometry) return
    const name = mesh.userData.meshName as string | undefined
    if (name && library.peekMesh(name) === geometry) return
    geometry.dispose()
  })
}
